// A project at a glance: how far along it is, what is overdue and due this week, its milestones, and - for the team -
// who carries what and the time it has taken. Leads add milestones and mark them reached here.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatShort } from "../../lib/chat";
import {
  COLUMNS, STATUS_DOT, activityText, dayLabel, formatMinutes, projectKeys, todayIso, type Milestone, type Overview, type Project,
} from "../../lib/projects";
import { Alert, Avatar, Button, Card, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";

export function ProjectOverview({ project, onOpen, onActivity, addMilestone = false }: {
  project: Project; onOpen: (taskId: string) => void; onActivity: () => void; addMilestone?: boolean;
}) {
  const { org } = useOrg();
  const data = useQuery({
    queryKey: projectKeys.overview(org.slug, project.id),
    queryFn: () => api<Overview>(`/orgs/${org.slug}/projects/${project.id}/overview`),
  });
  if (data.isLoading) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  if (data.error || !data.data) return <Alert>{errorText(data.error)}</Alert>;
  const o = data.data;
  const pct = o.total ? Math.round((o.done / o.total) * 100) : 0;
  const open = o.total - o.done;
  return (
    <div className="space-y-5 pb-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Done" value={`${pct}%`} hint={`${o.done} of ${o.total} tasks`} />
        <Stat label="Open" value={String(open)} hint={o.team ? `${o.team.unassigned} with nobody on them` : "still to do"} />
        <Stat label="Overdue" value={String(o.overdueCount)} hint={o.overdueCount ? "past their due date" : "nothing late"} tone={o.overdueCount ? "red" : undefined} />
        <Stat label="Due this week" value={String(o.dueSoon.length)} hint="in the next 7 days" />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <Milestones project={project} milestones={o.milestones} startAdding={addMilestone} />
        <Card className="p-0">
          <h2 className="px-5 pt-4 text-sm font-semibold">Coming up and late</h2>
          {!o.overdue.length && !o.dueSoon.length && <p className="px-5 pb-5 pt-2 text-sm text-slate-500">Nothing is due in the next week.</p>}
          <ul className="mt-2 divide-y divide-slate-100 pb-2 dark:divide-slate-800">
            {[...o.overdue, ...o.dueSoon].map((t) => {
              const late = Boolean(t.dueDate && t.dueDate < todayIso());
              return (
                <li key={t.id}>
                  <button type="button" onClick={() => onOpen(t.id)} className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <span className={cx("w-24 shrink-0 whitespace-nowrap text-xs font-medium", late ? "text-red-600" : "text-slate-500")}>{late ? "Late · " : ""}{t.dueDate ? dayLabel(t.dueDate) : ""}</span>
                    <span className="min-w-0 flex-1 truncate text-sm"><span className="font-mono text-xs text-slate-500">{t.ref}</span> {t.title}</span>
                    {t.assignee && <Avatar name={t.assignee.name} userId={t.assignee.id} size="sm" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {o.total > 0 && (
        <Card className="p-4">
          <div className="flex h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" role="img" aria-label={COLUMNS.map((c) => `${c.label}: ${o.status[c.status]}`).join(", ")}>
            {COLUMNS.map((c) => o.status[c.status] > 0 && <span key={c.status} className={STATUS_DOT[c.status]} style={{ width: `${(o.status[c.status] / o.total) * 100}%` }} />)}
          </div>
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
            {COLUMNS.map((c) => <span key={c.status} className="inline-flex items-center gap-1.5"><span className={cx("h-2 w-2 rounded-full", STATUS_DOT[c.status])} />{c.label} {o.status[c.status]}</span>)}
          </p>
        </Card>
      )}

      {o.team && (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card>
            <h2 className="text-sm font-semibold">Who carries what</h2>
            {!o.team.workload.length && <p className="mt-2 text-sm text-slate-500">No open task has anyone on it yet.</p>}
            <ul className="mt-3 space-y-2.5">
              {o.team.workload.map((w) => {
                const most = Math.max(...o.team!.workload.map((x) => x.open));
                return (
                  <li key={w.person.id} className="flex items-center gap-3">
                    <Avatar name={w.person.name} userId={w.person.id} size="sm" />
                    <span className="w-32 shrink-0 truncate text-sm">{w.person.name}</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><span className="block h-full rounded-full bg-brand-500" style={{ width: `${(w.open / most) * 100}%` }} /></span>
                    <span className="w-24 shrink-0 text-right text-xs text-slate-600 dark:text-slate-400">{w.open} open{w.overdue ? <span className="text-red-600"> · {w.overdue} late</span> : null}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
          <Card>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">Time</h2>
              {project.can.update && o.team.time.totalMinutes > 0 && (
                <a href={`/api/v1/orgs/${org.slug}/projects/${project.id}/time.csv`} className="text-sm text-brand-600 hover:underline"><Icon name="download" className="mr-1 inline h-4 w-4" />Export for invoicing</a>
              )}
            </div>
            <p className="mt-2 text-2xl font-semibold">{formatMinutes(o.team.time.totalMinutes)}<span className="ml-2 text-sm font-normal text-slate-500">{o.team.time.estimateMinutes ? `of ${formatMinutes(o.team.time.estimateMinutes)} estimated` : "logged"}</span></p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {o.team.time.byPerson.map((p, i) => (
                <li key={p.person?.id ?? i} className="flex justify-between gap-3"><span className="truncate">{p.person?.name ?? "Someone who left"}</span><span className="text-slate-600 dark:text-slate-400">{formatMinutes(p.minutes)}</span></li>
              ))}
            </ul>
            {!o.team.time.totalMinutes && <p className="mt-1 text-sm text-slate-500">Log time on a task to see it here.</p>}
          </Card>
        </div>
      )}

      <Card className="p-0">
        <div className="flex items-center justify-between px-5 pt-4">
          <h2 className="text-sm font-semibold">Lately</h2>
          <button type="button" onClick={onActivity} className="text-sm text-brand-600 hover:underline">All activity</button>
        </div>
        <ul className="mt-2 divide-y divide-slate-100 pb-2 dark:divide-slate-800">
          {o.recent.map((a) => (
            <li key={a.id} className="flex items-start gap-3 px-5 py-2.5 text-sm">
              <Avatar name={a.actor?.name ?? "?"} userId={a.actor?.id} size="sm" />
              <p className="min-w-0 flex-1"><span className="font-medium">{a.actor?.name ?? "Someone"}</span> {activityText(a, true)}</p>
              <span className="shrink-0 text-xs text-slate-400">{formatShort(a.createdAt)}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: "red" }) {
  return (
    <Card className="px-4 py-3">
      <p className="flex items-baseline gap-2">
        <span className={cx("text-2xl font-semibold", tone === "red" && "text-red-600")}>{value}</span>
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</span>
      </p>
      <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
    </Card>
  );
}

function Milestones({ project, milestones, startAdding }: { project: Project; milestones: Milestone[]; startAdding: boolean }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(startAdding && project.can.update);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { if (startAdding) box.current?.scrollIntoView({ block: "center" }); }, [startAdding]);
  const [error, setError] = useState<string | null>(null);
  const base = `/orgs/${org.slug}/projects/${project.id}/milestones`;
  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: projectKeys.overview(org.slug, project.id) }),
    qc.invalidateQueries({ queryKey: projectKeys.milestones(org.slug, project.id) }),
  ]);
  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try { await fn(); await refresh(); } catch (err) { setError(errorText(err)); }
  }
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    await run(() => api(base, { method: "POST", body: { name: String(d.get("name") ?? "").trim(), dueDate: String(d.get("dueDate") ?? "") || null } }));
    setAdding(false);
  }
  return (
    <Card>
      <div ref={box} className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Milestones</h2>
        {project.can.update && !adding && <Button size="sm" variant="ghost" onClick={() => setAdding(true)}><Icon name="plus" className="h-4 w-4" />Add</Button>}
      </div>
      {error && <div className="mt-2"><Alert>{error}</Alert></div>}
      {!milestones.length && !adding && <p className="mt-2 text-sm text-slate-500">{project.can.update ? "Mark the points the work is heading for, like “Design signed off”, and put tasks under them." : "None set yet."}</p>}
      <ul className="mt-3 space-y-4">
        {milestones.map((m) => {
          const pct = m.total ? Math.round((m.done / m.total) * 100) : 0;
          const late = !m.completedAt && m.dueDate && m.dueDate < todayIso();
          return (
            <li key={m.id}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className={cx("font-medium", m.completedAt && "text-emerald-700 dark:text-emerald-300")}>{m.completedAt && <Icon name="check" className="mr-1 inline h-4 w-4" />}{m.name}</span>
                {m.dueDate && <span className={cx("text-xs", late ? "font-medium text-red-600" : "text-slate-500")}>{late ? "Late · " : "Due "}{dayLabel(m.dueDate)}</span>}
                <span className="ml-auto text-xs text-slate-500">{m.done} of {m.total}</span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${m.name}: ${pct}%`}>
                <div className={cx("h-full rounded-full", m.completedAt ? "bg-emerald-500" : "bg-brand-600")} style={{ width: `${m.completedAt ? 100 : pct}%` }} />
              </div>
              {project.can.update && (
                <p className="mt-1.5 flex gap-3 text-xs">
                  <button type="button" className="text-brand-600 hover:underline" onClick={() => run(() => api(`${base}/${m.id}`, { method: "PATCH", body: { completed: !m.completedAt } }))}>{m.completedAt ? "Reopen" : "Mark reached"}</button>
                  <button type="button" className="text-red-600 hover:underline" onClick={() => { if (confirm(`Remove the milestone "${m.name}"? Its tasks stay.`)) void run(() => api(`${base}/${m.id}`, { method: "DELETE" })); }}>Remove</button>
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {adding && (
        <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-2">
          <label className="min-w-0 flex-1 text-sm"><span className="mb-1 block font-medium">Milestone</span>
            <input name="name" required maxLength={120} autoFocus placeholder="Design signed off" className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          </label>
          <label className="text-sm"><span className="mb-1 block font-medium">Due</span>
            <input name="dueDate" type="date" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          </label>
          <Button type="submit">Add</Button>
          <Button type="button" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
        </form>
      )}
    </Card>
  );
}
